import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import useGetEmailTemplate from "../../hooks/useGetEmailTemplate";
import MuPb from "../../widgets/MuPb";
import {
  getAuthHeader,
  getDbHeader,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import FullImageModal from "../../widgets/FullImageModal";
import MyQuill from "../../quill/MyQuill";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddIcon from "@mui/icons-material/Add";
import {
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { LoadingButton } from "@mui/lab";
import ImageCropperModal from "../../widgets/ImageCropperModal";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import axios from "axios";
import { useParams } from "react-router-dom";
const variables = ["emailId", "lastName", "firstName", "companyName", "qrCode"];
const EmailTemplate = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error, refetch } = useGetEmailTemplate(id);
  const [imageToShow, setImageToShow] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);
  const [propertyType, setPropertyType] = useState("");
  const [aspectRatio, setAspectRatio] = useState(2 / 1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showImage, setShowImage] = useState(false);
  const [emailBody, setEmailBody] = useState();
  const [emailSubject, setEmailSubject] = useState("");
  const [savingBody, setSavingBody] = useState(false);

  useEffect(() => {
    if (data?.emailTemplate) {
      setEmailBody(data.emailTemplate.body);
      setEmailSubject(data.emailTemplate.subject);
    }
  }, [data]);

  const handleEmailSave = useCallback(async () => {
    try {
      setSavingBody(true);
      const response = await axios.post(
        `${API_ENDPOINT}event/update-email-body`,
        { emailBody, emailSubject },
        getAuthHeader(id)
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSavingBody(false);
    }
  }, [emailBody, emailSubject]);

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }
  const handleCloseImage = () => {
    setShowImage(false);
  };
  const handleShowImage = (imgUrl) => {
    setImageToShow(imgUrl);
    setShowImage(true);
  };
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopy = (variable) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    handleClose();
  };

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    refetch();
  };
  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Typography my={2} variant="h6">
        Update Email Template <b>{data?.eventName}</b>
      </Typography>
      <Box>
        <Box sx={{ position: "relative", width: "100%" }}>
          <img
            style={{ width: "100%" }}
            src={data?.emailTemplate?.headerUrl}
            alt="Banner"
          />
          <Button
            onClick={() => {
              setPropertyType("emailTemplate.headerUrl");
              setAspectRatio(1198 / 199);
              setCropperOpen(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "white", // Optional: Improves visibility
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.8)" },
            }}
          >
            Change
          </Button>
        </Box>
        <Box my={2}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "16px",
              gap: "8px",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Update your email content
            </Typography>
            <Button variant="outlined" onClick={handleOpen} size="small">
              Vairiables to use
            </Button>

            {/* Popover Menu */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <List dense>
                {variables.map((variable) => (
                  <ListItem key={variable} disablePadding>
                    <ListItemButton onClick={() => handleCopy(variable)}>
                      <ListItemText primary={`{{${variable}}}`} />
                      <Tooltip title="Copy">
                        <IconButton edge="end">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Popover>
          </div>
          <TextField
            size="small"
            sx={{ my: 3 }}
            variant="outlined"
            label="Update Email Subject"
            value={emailSubject}
            onChange={(event) => {
              setEmailSubject(event.target.value);
            }}
          />
          <MyQuill value={emailBody} onChange={setEmailBody} />
        </Box>
        <Stack sx={{ mb: 2 }} direction={"row"}>
          <Box flex={1}></Box>
          <LoadingButton
            variant="outlined"
            loading={savingBody}
            onClick={handleEmailSave}
            startIcon={<SaveOutlinedIcon fontSize="small" />}
          >
            Save
          </LoadingButton>
        </Stack>
        <Box sx={{ position: "relative", width: "100%" }}>
          <img
            style={{ width: "100%" }}
            src={data?.emailTemplate?.footerUrl}
            alt="Banner"
          />
          <Button
            onClick={() => {
              setPropertyType("emailTemplate.footerUrl");
              setAspectRatio(1198 / 199);
              setCropperOpen(true);
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "white", // Optional: Improves visibility
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.8)" },
            }}
          >
            Change
          </Button>
        </Box>
      </Box>

      <FullImageModal
        imgUrl={imageToShow}
        open={showImage}
        handleClose={handleCloseImage}
      />
      <ImageCropperModal
        open={cropperOpen}
        type={propertyType}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}event/upload-image-email-template/${propertyType}`}
        headers={{
          dbauth: id, //db: testapp
        }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />
    </Container>
  );
};

export default EmailTemplate;
